"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getRuleV2FromV1 = exports.getRuleSetRevisionV2FromV1 = exports.getRuleSetRevisionV2Serializer = void 0;
const umi_1 = require("@metaplex-foundation/umi");
const serializers_1 = require("@metaplex-foundation/umi/serializers");
const additionalSigner_1 = require("./additionalSigner");
const all_1 = require("./all");
const amount_1 = require("./amount");
const any_1 = require("./any");
const namespace_1 = require("./namespace");
const not_1 = require("./not");
const pass_1 = require("./pass");
const pdaMatch_1 = require("./pdaMatch");
const programOwned_1 = require("./programOwned");
const programOwnedList_1 = require("./programOwnedList");
const programOwnedTree_1 = require("./programOwnedTree");
const pubkeyListMatch_1 = require("./pubkeyListMatch");
const pubkeyMatch_1 = require("./pubkeyMatch");
const pubkeyTreeMatch_1 = require("./pubkeyTreeMatch");
const rule_1 = require("./rule");
const getRuleSetRevisionV2Serializer = () => ({
    description: 'RuleSetRevisionV2',
    fixedSize: null,
    maxSize: null,
    serialize: (revision) => {
        const tuples = Object.entries(revision.operations);
        const operations = tuples.map(([operation]) => operation);
        const rules = tuples.map(([, rule]) => rule);
        const ruleSize = operations.length;
        return (0, serializers_1.mergeBytes)([
            (0, serializers_1.u32)().serialize(2),
            (0, serializers_1.u32)().serialize(ruleSize),
            (0, serializers_1.publicKey)().serialize(revision.owner),
            (0, serializers_1.string)({ size: 32 }).serialize(revision.name),
            (0, serializers_1.array)((0, serializers_1.string)({ size: 32 }), { size: ruleSize }).serialize(operations),
            (0, serializers_1.array)((0, rule_1.getRuleV2Serializer)(), { size: ruleSize }).serialize(rules), // rules
        ]);
    },
    deserialize: (buffer, offset = 0) => {
        const [libVersion, versionOffset] = (0, serializers_1.u32)().deserialize(buffer, offset);
        offset = versionOffset;
        if (libVersion !== 2) {
            throw new Error(`Expected a rule set version 2, got version ${libVersion}`);
        }
        const [ruleSize, ruleSizeOffset] = (0, serializers_1.u32)().deserialize(buffer, offset);
        offset = ruleSizeOffset;
        const [owner, ownerOffset] = (0, serializers_1.publicKey)().deserialize(buffer, offset);
        offset = ownerOffset;
        const [name, nameOffset] = (0, serializers_1.string)({ size: 32 }).deserialize(buffer, offset);
        offset = nameOffset;
        const [operations, operationsOffset] = (0, serializers_1.array)((0, serializers_1.string)({ size: 32 }), {
            size: ruleSize,
        }).deserialize(buffer, offset);
        offset = operationsOffset;
        const [rules, rulesOffset] = (0, serializers_1.array)((0, rule_1.getRuleV2Serializer)(), {
            size: ruleSize,
        }).deserialize(buffer, offset);
        offset = rulesOffset;
        const tuples = operations.map((operation, index) => [
            operation,
            rules[index],
        ]);
        return [
            {
                libVersion: 2,
                name,
                owner,
                operations: Object.fromEntries(tuples),
            },
            offset,
        ];
    },
});
exports.getRuleSetRevisionV2Serializer = getRuleSetRevisionV2Serializer;
const getRuleSetRevisionV2FromV1 = (ruleSetV1) => ({
    libVersion: 2,
    name: ruleSetV1.ruleSetName,
    owner: (0, umi_1.publicKey)(new Uint8Array(ruleSetV1.owner)),
    operations: Object.fromEntries(Object.entries(ruleSetV1.operations).map(([operation, rule]) => [
        operation,
        (0, exports.getRuleV2FromV1)(rule),
    ])),
});
exports.getRuleSetRevisionV2FromV1 = getRuleSetRevisionV2FromV1;
const getRuleV2FromV1 = (ruleV1) => {
    const toPublicKey = (bytes) => (0, umi_1.publicKey)(new Uint8Array(bytes));
    if (ruleV1 === 'Namespace') {
        return (0, namespace_1.namespaceV2)();
    }
    if (ruleV1 === 'Pass') {
        return (0, pass_1.passV2)();
    }
    if ('AdditionalSigner' in ruleV1) {
        return (0, additionalSigner_1.additionalSignerV2)(toPublicKey(ruleV1.AdditionalSigner.account));
    }
    if ('All' in ruleV1) {
        return (0, all_1.allV2)(ruleV1.All.rules.map(exports.getRuleV2FromV1));
    }
    if ('Amount' in ruleV1) {
        return (0, amount_1.amountV2)(ruleV1.Amount.field, ruleV1.Amount.operator, ruleV1.Amount.amount);
    }
    if ('Any' in ruleV1) {
        return (0, any_1.anyV2)(ruleV1.Any.rules.map(exports.getRuleV2FromV1));
    }
    if ('Not' in ruleV1) {
        return (0, not_1.notV2)((0, exports.getRuleV2FromV1)(ruleV1.Not.rule));
    }
    if ('PDAMatch' in ruleV1) {
        return (0, pdaMatch_1.pdaMatchV2)(ruleV1.PDAMatch.pda_field, toPublicKey(ruleV1.PDAMatch.program), ruleV1.PDAMatch.seeds_field);
    }
    if ('ProgramOwned' in ruleV1) {
        return (0, programOwned_1.programOwnedV2)(ruleV1.ProgramOwned.field, toPublicKey(ruleV1.ProgramOwned.program));
    }
    if ('ProgramOwnedList' in ruleV1) {
        return (0, programOwnedList_1.programOwnedListV2)(ruleV1.ProgramOwnedList.field, ruleV1.ProgramOwnedList.programs.map((p) => toPublicKey(p)));
    }
    if ('ProgramOwnedTree' in ruleV1) {
        return (0, programOwnedTree_1.programOwnedTreeV2)(ruleV1.ProgramOwnedTree.pubkey_field, ruleV1.ProgramOwnedTree.proof_field, new Uint8Array(ruleV1.ProgramOwnedTree.root));
    }
    if ('PubkeyListMatch' in ruleV1) {
        return (0, pubkeyListMatch_1.pubkeyListMatchV2)(ruleV1.PubkeyListMatch.field, ruleV1.PubkeyListMatch.pubkeys.map((p) => toPublicKey(p)));
    }
    if ('PubkeyMatch' in ruleV1) {
        return (0, pubkeyMatch_1.pubkeyMatchV2)(ruleV1.PubkeyMatch.field, toPublicKey(ruleV1.PubkeyMatch.pubkey));
    }
    if ('PubkeyTreeMatch' in ruleV1) {
        return (0, pubkeyTreeMatch_1.pubkeyTreeMatchV2)(ruleV1.PubkeyTreeMatch.pubkey_field, ruleV1.PubkeyTreeMatch.proof_field, new Uint8Array(ruleV1.PubkeyTreeMatch.root));
    }
    throw new Error(`Unknown rule: ${JSON.stringify(ruleV1)}`);
};
exports.getRuleV2FromV1 = getRuleV2FromV1;
//# sourceMappingURL=revision.js.map